import { useEffect, useRef } from 'react';

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  radius: number;
  color: string;
  opacity: number;
  glow: number;
}

const ParticleBackground = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // キャンバスサイズを設定
    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    // パーティクルの配列
    const particles: Particle[] = [];
    const particleCount = 150;

    // カラーパレット（青系のみ）
    const colors = [
      '#3B82F6', // 青 (blue-500)
      '#06B6D4', // シアン (cyan-500)
      '#60A5FA', // ライトブルー (blue-400)
      '#1E40AF', // ダークブルー (blue-800)
      '#0891B2', // ダークシアン (cyan-600)
      '#93C5FD', // ライトブルー (blue-300)
      '#0EA5E9', // スカイブルー (sky-500)
      '#2563EB', // ブルー (blue-600)
    ];

    // パーティクルを初期化
    for (let i = 0; i < particleCount; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        vx: (Math.random() - 0.5) * 0.5,
        vy: (Math.random() - 0.5) * 0.5,
        radius: Math.random() * 1.5 + 1, // 少し大きくしてシャープに
        color: colors[Math.floor(Math.random() * colors.length)],
        opacity: Math.random() * 0.9 + 0.5, // より不透明に
        glow: Math.random() * 3 + 2, // グロー効果を小さく（5-15 → 2-5）
      });
    }

    // 波のような流れを作る関数
    const createWaveFlow = (particle: Particle, time: number) => {
      const waveX = particle.x + Math.sin(time * 0.001 + particle.y * 0.01) * 50;
      const waveY = particle.y + Math.cos(time * 0.001 + particle.x * 0.01) * 30;
      return { waveX, waveY };
    };

    // アニメーションループ
    let animationFrameId: number;
    let time = 0;

    const animate = () => {
      time += 1;
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // 背景のグラデーション（宇宙感）
      const gradient = ctx.createRadialGradient(
        canvas.width / 2,
        canvas.height / 2,
        0,
        canvas.width / 2,
        canvas.height / 2,
        Math.max(canvas.width, canvas.height)
      );
      gradient.addColorStop(0, 'rgba(15, 23, 42, 0.3)'); // slate-900
      gradient.addColorStop(0.5, 'rgba(30, 58, 138, 0.5)'); // blue-900
      gradient.addColorStop(1, 'rgba(15, 23, 42, 0.8)'); // slate-900
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // パーティクルを描画
      particles.forEach((particle, index) => {
        // 波のような流れを適用
        const { waveX, waveY } = createWaveFlow(particle, time + index * 10);

        // パーティクルの位置を更新
        particle.x += particle.vx;
        particle.y += particle.vy;

        // 画面外に出たら反対側に
        if (particle.x < 0) particle.x = canvas.width;
        if (particle.x > canvas.width) particle.x = 0;
        if (particle.y < 0) particle.y = canvas.height;
        if (particle.y > canvas.height) particle.y = 0;

        // グロー効果（より控えめに）
        const glowGradient = ctx.createRadialGradient(
          waveX,
          waveY,
          0,
          waveX,
          waveY,
          particle.glow
        );
        glowGradient.addColorStop(0, particle.color + 'FF'); // 完全に不透明
        glowGradient.addColorStop(0.3, particle.color + '60'); // より早くフェード
        glowGradient.addColorStop(1, particle.color + '00'); // 完全に透明

        ctx.fillStyle = glowGradient;
        ctx.beginPath();
        ctx.arc(waveX, waveY, particle.glow, 0, Math.PI * 2);
        ctx.fill();

        // パーティクル本体（よりシャープに）
        ctx.fillStyle = particle.color;
        ctx.globalAlpha = particle.opacity;
        ctx.beginPath();
        ctx.arc(waveX, waveY, particle.radius, 0, Math.PI * 2);
        ctx.fill();
        ctx.globalAlpha = 1;
        
        // エッジをシャープにするために小さなハイライトを追加
        ctx.fillStyle = '#FFFFFF';
        ctx.globalAlpha = particle.opacity * 0.3;
        ctx.beginPath();
        ctx.arc(waveX - particle.radius * 0.3, waveY - particle.radius * 0.3, particle.radius * 0.3, 0, Math.PI * 2);
        ctx.fill();
        ctx.globalAlpha = 1;

        // 近くのパーティクル同士を線でつなぐ（粒子の波の効果）
        particles.slice(index + 1).forEach((otherParticle) => {
          const dx = waveX - (otherParticle.x + Math.sin(time * 0.001 + otherParticle.y * 0.01) * 50);
          const dy = waveY - (otherParticle.y + Math.cos(time * 0.001 + otherParticle.x * 0.01) * 30);
          const distance = Math.sqrt(dx * dx + dy * dy);

          if (distance < 100) {
            ctx.strokeStyle = particle.color;
            ctx.globalAlpha = (1 - distance / 100) * 0.2;
            ctx.lineWidth = 0.5;
            ctx.beginPath();
            ctx.moveTo(waveX, waveY);
            ctx.lineTo(
              otherParticle.x + Math.sin(time * 0.001 + otherParticle.y * 0.01) * 50,
              otherParticle.y + Math.cos(time * 0.001 + otherParticle.x * 0.01) * 30
            );
            ctx.stroke();
            ctx.globalAlpha = 1;
          }
        });
      });

      animationFrameId = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      cancelAnimationFrame(animationFrameId);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 w-full h-full"
      style={{ zIndex: 5 }}
    />
  );
};

export default ParticleBackground;

